import {
  User,
  auth,
  getRedirectResult,
  onAuthStateChanged,
} from "@/lib/firebase";
import { UserProfile } from "@/types/user";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  isPremium: boolean;
  isPlatinum: boolean;
  isAdmin: boolean;
  membershipLevel: "guest" | "free" | "premium" | "platinum" | null;
  refetchProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userProfile: null,
  loading: true,
  isAuthenticated: false,
  isPremium: false,
  isPlatinum: false,
  isAdmin: false,
  membershipLevel: null,
  refetchProfile: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (firebaseUser: User, retryCount = 0) => {
    try {
      // Use /api/me to fetch/create user in D1 DB (same as nickname API)
      // This ensures consistent user creation across all login methods
      const { getIdToken } = await import("@/lib/firebase");
      const token = await getIdToken(true); // Force refresh token

      if (!token) {
        throw new Error("Failed to get ID token");
      }

      console.log("[AuthContext] Fetching user profile from /api/me");
      const res = await fetch("/api/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error("[AuthContext] /api/me failed:", {
          status: res.status,
          statusText: res.statusText,
          body: errorText,
        });
        throw new Error(
          `Failed to fetch user profile: ${res.status} - ${errorText}`
        );
      }

      const userData = await res.json();
      console.log("[AuthContext] /api/me success:", {
        email: userData.email,
        nickname: userData.nickname,
        nicknameDisplay: userData.nicknameDisplay,
        communityDisplayName: userData.communityDisplayName,
        uid: userData.uid,
      });

      // Convert /api/me response to UserProfile format
      const profile: UserProfile = {
        uid: userData.uid || firebaseUser.uid,
        email: userData.email || firebaseUser.email || "",
        displayName:
          userData.communityDisplayName ||
          userData.nicknameDisplay ||
          userData.displayName ||
          firebaseUser.displayName ||
          "사용자",
        nickname: userData.nickname || null,
        nicknameDisplay: userData.nicknameDisplay || null,
        nicknameFixed: Number(userData.nicknameFixed || 0),
        nicknameRequired:
          typeof userData.nicknameRequired === "boolean"
            ? userData.nicknameRequired
            : undefined,
        suburb: userData.suburb || null,
        regionCode: userData.regionCode || null,
        selectedRegion: userData.regionCode || undefined,
        location: userData.regionCode
          ? {
              state: userData.regionCode,
              suburb: userData.suburb || undefined,
            }
          : undefined,
        photoURL: userData.picture || firebaseUser.photoURL,
        role: userData.role || "free",
        emailFrequency: null, // TODO: Add to /api/me response if needed
        preferredCategories: [], // TODO: Add to /api/me response if needed
        usageTracking: {
          wordLookups: 0,
          quizzesTaken: 0,
          lastReset: new Date().toISOString(),
        },
        stripeCustomerId: null, // TODO: Add to /api/me response if needed
        stripeSubscriptionId: null, // TODO: Add to /api/me response if needed
        subscriptionStatus: null, // TODO: Add to /api/me response if needed
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      setUserProfile(profile);
    } catch (error: any) {
      console.error("[AuthContext] Error fetching user profile:", error);

      // Retry once if network error
      if (
        retryCount === 0 &&
        (error?.message?.includes("network") ||
          error?.message?.includes("Failed to fetch"))
      ) {
        console.log("[AuthContext] Retrying profile fetch after delay...");
        await new Promise((resolve) => setTimeout(resolve, 500));
        return fetchProfile(firebaseUser, 1);
      }

      console.warn(
        "[AuthContext] /api/me unavailable - using fallback profile from Auth"
      );

      // Fallback: Use Auth data directly when /api/me is unavailable
      const fallbackProfile: UserProfile = {
        uid: firebaseUser.uid,
        email: firebaseUser.email || "",
        displayName: firebaseUser.displayName || "사용자",
        photoURL: firebaseUser.photoURL,
        role: "free", // Default to free tier
        emailFrequency: null,
        preferredCategories: [],
        usageTracking: {
          wordLookups: 0,
          quizzesTaken: 0,
          lastReset: new Date().toISOString(),
        },
        stripeCustomerId: null,
        stripeSubscriptionId: null,
        subscriptionStatus: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setUserProfile(fallbackProfile);
    }
  };

  const refetchProfile = async () => {
    if (user) {
      await fetchProfile(user);
    }
  };

  useEffect(() => {
    if (!auth) {
      console.warn("Firebase Auth is not initialized");
      setLoading(false);
      return;
    }

    // Handle Google OAuth redirect result
    const handleRedirectResult = async () => {
      if (!auth) return;

      try {
        console.log("[AuthContext] Checking for Google redirect result...");
        const result = await getRedirectResult(auth);
        if (result) {
          console.log("[AuthContext] ✅ Google redirect result received:", {
            user: result.user?.email,
            uid: result.user?.uid,
            providerId: result.providerId,
          });

          // User is already signed in via onAuthStateChanged
          // The fetchProfile will be called automatically by onAuthStateChanged
          // which will create the user in D1 DB via /api/me
        } else {
          console.log(
            "[AuthContext] No redirect result (normal page load or not a redirect)"
          );
        }
      } catch (error: any) {
        console.error("[AuthContext] ❌ Error handling redirect result:", {
          code: error?.code,
          message: error?.message,
          stack: error?.stack,
        });

        // Common errors:
        // - auth/account-exists-with-different-credential: User already exists with different provider
        // - auth/operation-not-allowed: Google sign-in not enabled
        // - auth/popup-closed-by-user: User closed the popup
        // - auth/unauthorized-domain: Domain not authorized

        if (error?.code === "auth/account-exists-with-different-credential") {
          console.error(
            "[AuthContext] Account exists with different credential - user needs to link accounts"
          );
        } else if (error?.code === "auth/operation-not-allowed") {
          console.error(
            "[AuthContext] Google sign-in not enabled in Firebase console"
          );
        } else if (error?.code === "auth/unauthorized-domain") {
          console.error(
            "[AuthContext] Domain not authorized - check Firebase console"
          );
        }

        // Don't set loading to false here - let onAuthStateChanged handle it
        // The error might be recoverable (e.g., user can try again)
      }
    };

    // Check for redirect result first (before setting up onAuthStateChanged)
    handleRedirectResult();

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log("[AuthContext] onAuthStateChanged triggered:", {
        hasUser: !!firebaseUser,
        uid: firebaseUser?.uid || null,
        email: firebaseUser?.email || null,
      });

      setUser(firebaseUser);

      if (firebaseUser) {
        // Reload user to get latest Auth updates from server
        try {
          await firebaseUser.reload();
          console.log("[AuthContext] User reloaded successfully");
        } catch (error) {
          console.warn("[AuthContext] Failed to reload user:", error);
        }

        // Fetch profile from /api/me (creates user in D1 DB if needed)
        await fetchProfile(firebaseUser);
      } else {
        console.log("[AuthContext] No user - clearing profile");
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const getMembershipLevel = ():
    | "guest"
    | "free"
    | "premium"
    | "platinum"
    | null => {
    if (!user) return "guest";
    if (!userProfile) return null;
    if (userProfile.role === "platinum") return "platinum";
    if (userProfile.role === "premium") return "premium";
    if (userProfile.role === "free") return "free";
    return "guest";
  };

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    isAuthenticated: !!user,
    isPremium:
      userProfile?.role === "premium" || userProfile?.role === "platinum",
    isPlatinum: userProfile?.role === "platinum",
    isAdmin: userProfile?.role === "admin",
    membershipLevel: getMembershipLevel(),
    refetchProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
